import React from 'react';
import { Link } from 'react-router-dom';
import { fetchArticles, sanityEnabled } from '../lib/sanity';
import { ArrowUpRight } from 'lucide-react';

function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-AU', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

export default function Articles() {
  const [articles, setArticles] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let mounted = true;
    setLoading(true);
    fetchArticles()
      .then((data) => { if (mounted) setArticles(data); })
      .catch((e) => { if (mounted) setError(e.message); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  return (
    <div data-testid="articles-page">
      {/* HEADER */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 lg:pt-28 pb-12">
        <div className="font-mono text-[10px] tracking-widest uppercase text-ink-muted mb-4">
          Articles
        </div>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <h1 className="font-display font-bold tracking-tighter text-5xl md:text-7xl text-ink max-w-3xl leading-[0.95]">
            Field notes from<br />engineering rooms.
          </h1>
          <p className="text-ink-muted max-w-md leading-relaxed">
            Long-form notes on building, securing and training AI systems —
            from real client engagements, not vendor blog posts.
          </p>
        </div>
        {!sanityEnabled && (
          <div
            data-testid="articles-cms-notice"
            className="mt-10 border border-dashed border-black/20 bg-cream-alt p-4 text-xs text-ink-muted font-mono tracking-tight"
          >
            Showing example articles. Connect Sanity by setting{' '}
            <span className="text-ink">REACT_APP_SANITY_PROJECT_ID</span> in your environment.
          </div>
        )}
      </section>

      {/* GRID */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        {loading && (
          <div data-testid="articles-loading" className="py-24 text-center font-mono text-xs tracking-widest uppercase text-ink-muted">
            Loading articles…
          </div>
        )}
        {error && !loading && (
          <div data-testid="articles-error" className="py-24 text-center text-red-700">
            {error}
          </div>
        )}
        {!loading && !error && articles.length === 0 && (
          <div data-testid="articles-empty" className="py-24 text-center text-ink-muted">
            No articles yet.
          </div>
        )}
        {!loading && articles.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-black/10 border border-black/10">
            {articles.map((a) => (
              <Link
                to={`/articles/${a.slug.current}`}
                key={a._id}
                data-testid={`article-card-${a.slug.current}`}
                className="group bg-cream p-8 flex flex-col hover:bg-cream-alt transition-colors"
              >
                {a.coverImageUrl && (
                  <div className="aspect-[4/3] overflow-hidden mb-6 border border-black/10">
                    <img
                      src={a.coverImageUrl}
                      alt={a.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                )}
                <div className="flex items-center gap-3 mb-4">
                  {a.category && (
                    <span className="font-mono text-[10px] tracking-widest uppercase text-ember">
                      {a.category}
                    </span>
                  )}
                  <span className="font-mono text-[10px] tracking-widest uppercase text-ink-muted">
                    {formatDate(a.publishedAt)}
                  </span>
                </div>
                <h2 className="font-display font-semibold tracking-tight text-2xl text-ink mb-3 group-hover:text-ember transition-colors">
                  {a.title}
                </h2>
                {a.excerpt && (
                  <p className="text-ink-muted leading-relaxed mb-6 flex-1">{a.excerpt}</p>
                )}
                <div className="flex items-center gap-2 font-mono text-xs tracking-widest uppercase text-ink">
                  Read article
                  <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
